import { useId, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api, type ApiError } from '../lib/api.js';
import { useScriptSubmission } from '../lib/useScriptSubmission.js';
import { Callout } from './Callout.js';
import { Icon, type IconName } from './Icon.js';
import { AddRequirementDialog, type SelectableScene } from './AddRequirementDialog.js';
import { ScriptDropZone } from './ScriptDropZone.js';
import { ScriptPrivacyNotice } from './ScriptPrivacyNotice.js';

export type IntakeMethod = 'UPLOAD' | 'PASTE' | 'MANUAL';

/**
 * THE THREE WAYS A PRODUCTION CAN START.
 *
 * The third one needs saying out loud: a requirement takes a title and nothing
 * else, so a producer with no screenplay — a commercial, a music video, a
 * treatment that does not exist yet — has always been able to work here.
 */
export const INTAKE_METHODS: readonly { id: IntakeMethod; label: string; sub: string; icon: IconName }[] = [
  { id: 'UPLOAD', label: 'Upload a file', sub: 'PDF, FDX, DOCX, TXT', icon: 'upload' },
  { id: 'PASTE', label: 'Paste text', sub: 'Script, brief or shot list', icon: 'script' },
  { id: 'MANUAL', label: 'Add manually', sub: 'Location requirements', icon: 'pencil' },
];

/**
 * ONE CHOICE OF THREE, SO A RADIO GROUP.
 *
 * Exactly one is selected, the arrow keys move between them and select as they
 * go, and the group is one tab stop — the pattern a screen reader announces as
 * "1 of 3, selected". They were buttons with `aria-pressed`, which reads as
 * three independent toggles.
 */
export function IntakeMethodSelector({
  value,
  onChange,
  disabled = false,
}: {
  value: IntakeMethod;
  onChange: (method: IntakeMethod) => void;
  /** While a screenplay is being sent: switching then would start a second one. */
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, step: number) => {
    const next = (from + step + INTAKE_METHODS.length) % INTAKE_METHODS.length;
    onChange(INTAKE_METHODS[next]!.id);
    refs.current[next]?.focus();
  };

  return (
    <div
      className="intake-methods"
      role="radiogroup"
      aria-label="How to add your script"
      aria-disabled={disabled || undefined}
      data-testid="intake-methods"
    >
      {INTAKE_METHODS.map((method, index) => {
        const on = value === method.id;

        return (
          <button
            key={method.id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            disabled={disabled && !on}
            className="intake-method"
            data-testid={`script-source-${method.id}`}
            data-on={on ? '1' : '0'}
            onClick={() => {
              if (!disabled) onChange(method.id);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(index, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(index, -1);
              }
            }}
          >
            <span className="intake-method-icon" aria-hidden="true">
              <Icon name={method.icon} size={20} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span className="intake-method-label">{method.label}</span>
              <span className="intake-method-sub">{method.sub}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Mirrors `MAX_PASTED_CHARS` in the server's upload validator. */
export const MAX_PASTED_CHARS = 2_000_000;

/** Why pasted text cannot be sent yet; null when it can. */
export function pastedTextProblem(text: string): string | null {
  if (text.length > MAX_PASTED_CHARS) {
    return `That is ${text.length.toLocaleString('en-IE')} characters — pasted text is limited to 2,000,000. Upload the file instead.`;
  }

  return null;
}

/**
 * Pasted screenplay text, with how much is there in the two units a producer
 * thinks in. The page figure is an estimate and says so — a screenplay page is
 * roughly 1,800 characters, and "118 pages" would be inventing precision.
 *
 * `actions` sit at the start of the footer row, where the Script tab keeps its
 * own submit; step 1 submits from the page's action bar instead.
 */
export function ScriptPasteEditor({
  value,
  onChange,
  disabled = false,
  actions,
}: {
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
  actions?: ReactNode;
}) {
  const id = useId();
  const problem = pastedTextProblem(value);

  return (
    <div className="intake-paste">
      <label className="sr-only" htmlFor={`${id}-text`}>
        Screenplay, treatment or shot list
      </label>
      <textarea
        id={`${id}-text`}
        className="intake-textarea"
        rows={12}
        value={value}
        disabled={disabled}
        data-testid="script-paste-input"
        placeholder="Paste your screenplay, treatment or shot list here..."
        aria-invalid={problem === null ? undefined : true}
        aria-describedby={`${id}-count`}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
      <div className="intake-paste-foot">
        {actions}
        {value.length > 0 && !disabled ? (
          <button
            type="button"
            className="ctl"
            data-testid="script-paste-clear"
            onClick={() => {
              onChange('');
            }}
          >
            Clear
          </button>
        ) : null}
        <span className="mm" id={`${id}-count`} style={{ fontSize: 12, marginLeft: 'auto' }} data-testid="script-paste-count">
          {value.length === 0
            ? 'Nothing pasted yet'
            : `${value.length.toLocaleString('en-IE')} characters · roughly ${String(Math.max(1, Math.round(value.length / 1800)))} pages`}
        </span>
      </div>
      {problem === null ? null : (
        <p className="intake-error" role="alert" data-testid="script-paste-error">
          {problem}
        </p>
      )}
    </div>
  );
}

/**
 * Why a screenplay was turned away.
 *
 * NOT "danger" when the answer is "buy the thing". A plan refusal is an offer;
 * dressing it as a failure makes a producer think the file was broken and try
 * again with another one.
 */
export function ScriptRefusal({ refusal, onRetry = null }: { refusal: ApiError; onRetry?: (() => void) | null }) {
  const needsPlan = refusal.code === 'AI_NOT_IN_PLAN' || refusal.code === 'SCRIPT_ALLOWANCE_USED';
  // The network or the server, not a verdict on the file: say so, or the
  // producer goes looking for something wrong with a screenplay that is fine.
  const notSent = refusal.status === 0 || refusal.status >= 500;

  return (
    <div style={{ marginTop: 14 }}>
      <Callout
        tone={needsPlan ? 'ok' : 'danger'}
        title={
          needsPlan
            ? 'The AI scout is not in this plan'
            : notSent
              ? 'The screenplay could not be sent'
              : 'That screenplay was refused'
        }
        testId="upload-error"
      >
        {refusal.message}
        {needsPlan ? (
          <span style={{ display: 'block', marginTop: 8 }}>
            Open <strong>Plan &amp; billing</strong> to see what Lite and Company include.
          </span>
        ) : refusal.code ? (
          <span className="mm" style={{ display: 'block', marginTop: 6, fontSize: 11.5 }}>
            {refusal.code}
          </span>
        ) : null}
        {onRetry === null ? null : (
          <span style={{ display: 'block', marginTop: 10 }}>
            <button type="button" className="ctl" data-testid="upload-retry" onClick={onRetry}>
              Try again
            </button>
          </span>
        )}
      </Callout>
    </div>
  );
}

/** Said when the "new" screenplay was byte-for-byte one the production already had. */
export function DuplicateScriptNotice() {
  return (
    <div style={{ marginTop: 14 }}>
      <Callout tone="info" title="Already added" testId="upload-duplicate">
        This is byte-for-byte a screenplay already on the production, so the existing version was
        reused rather than creating a duplicate.
      </Callout>
    </div>
  );
}

/**
 * HOW A SCREENPLAY GETS IN, on the Script tab.
 *
 * Built from the same pieces as step 1 (`pages/StartProductionPage.tsx`) — the
 * method selector, the drop zone, the paste editor and the submission hook — so
 * the two cannot drift. What differs is the frame: here the paste submit sits
 * under the text, and "Add manually" opens the requirement dialog with the
 * screenplay's scenes to scope it to.
 *
 * It owns the submitting and nothing else. What happens AFTER a screenplay
 * lands is the caller's to decide.
 */
export function ScriptIntake({
  projectId,
  scenes = [],
  onSubmitted,
  onRequirementAdded,
}: {
  projectId: string;
  /** Scope for a hand-written requirement. Empty when there is no screenplay
   *  yet — which the dialog allows: a unit base belongs to the production
   *  rather than to any scene. */
  scenes?: readonly SelectableScene[];
  onSubmitted: (scriptVersionId: string) => void;
  onRequirementAdded: () => void;
}) {
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<IntakeMethod>('UPLOAD');
  const [pasted, setPasted] = useState('');
  const [addingRequirement, setAddingRequirement] = useState(false);

  const submission = useScriptSubmission(projectId, (result) => {
    setPasted('');
    onSubmitted(result.scriptVersionId);
  });

  const addRequirement = useMutation({
    mutationFn: (input: { title: string; sceneIds: readonly string[] }) =>
      api.addRequirement(projectId, { title: input.title, sceneIds: input.sceneIds }),
    onSuccess: async () => {
      setAddingRequirement(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['requirements', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['review-progress', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['matchable', projectId] }),
      ]);
      onRequirementAdded();
    },
  });

  return (
    <div data-testid="script-intake" style={{ marginTop: 20 }}>
      <IntakeMethodSelector
        value={method}
        onChange={(next) => {
          setMethod(next);
          if (next === 'MANUAL' && method !== 'MANUAL') setAddingRequirement(true);
        }}
      />

      <div style={{ marginTop: 16 }}>
        {method === 'UPLOAD' ? (
          <ScriptDropZone
            busy={submission.uploading}
            file={submission.uploadingFile}
            progress={submission.progress}
            onFile={submission.upload}
            onCancel={submission.cancelUpload}
          />
        ) : null}

        {method === 'PASTE' ? (
          <ScriptPasteEditor
            value={pasted}
            onChange={setPasted}
            disabled={submission.pasting}
            actions={
              <button
                type="button"
                className="btn btn-primary"
                data-testid="script-paste-submit"
                disabled={pasted.trim().length === 0 || submission.pasting || pastedTextProblem(pasted) !== null}
                onClick={() => {
                  submission.paste(pasted);
                }}
              >
                {submission.pasting ? 'Reading…' : 'Use this text'}
              </button>
            }
          />
        ) : null}

        {method === 'MANUAL' ? (
          <div className="intake-manual-cta" data-testid="script-manual">
            <p className="mm" style={{ margin: 0, fontSize: 13, lineHeight: 1.6 }}>
              Add a location the production needs by hand — with or without a screenplay.
            </p>
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="script-manual-open"
              onClick={() => {
                setAddingRequirement(true);
              }}
            >
              Add requirement
            </button>
          </div>
        ) : null}
      </div>

      {addingRequirement ? (
        <AddRequirementDialog
          scenes={scenes}
          busy={addRequirement.isPending}
          error={addRequirement.error ? addRequirement.error.message : null}
          onCancel={() => {
            setAddingRequirement(false);
          }}
          onSubmit={(input) => {
            addRequirement.mutate(input);
          }}
        />
      ) : null}

      <ScriptPrivacyNotice />

      {submission.pasting ? (
        <p className="mm" style={{ marginTop: 12 }} data-testid="script-reading">
          Reading the screenplay…
        </p>
      ) : null}

      {submission.refusal ? <ScriptRefusal refusal={submission.refusal} onRetry={submission.retryUpload} /> : null}
      {submission.duplicate ? <DuplicateScriptNotice /> : null}
    </div>
  );
}
